import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, Button, Spinner } from '@/components/ui';
import { StandardReportSections } from '@/components/reports/standard/StandardReportSections';
import { StandardRunFailedAlert } from './StandardRunFailedAlert';
import { standardReportsService, saveBlob } from '@/services/standardReports.service';
import { getErrorMessage } from '@/services/apiClient';
import { isTerminalRunStatus } from '@/types/standardReports.types';
import type { StandardReportRun } from '@/types/standardReports.types';

/**
 * One standard report run that has already happened.
 *
 * WHY THIS PAGE EXISTS. Nothing opened a finished standard run. My Reports
 * lists a bare catalog run as its own row, and the runs page's View action and
 * row click both pointed at `/reports/standard/{key}?run={id}` — but the runner
 * reads only `reportKey` from the route and never looks at `run`, so all three
 * landed on an empty "configure and run" form instead of the result.
 *
 * IT IS ROUTED UNGATED, beside the custom and AI result viewers, and NOT inside
 * the Reports permission the runner sits behind. My Reports is reachable
 * without that permission deliberately, so a viewer it links into that demands
 * the permission is a row which silently does nothing when clicked.
 *
 * Rendering goes through StandardReportSections — the one component that knows
 * what a section looks like. The runner points it at a run it just fired; this
 * points it at a run someone fired last month. Same result, same shape.
 */

/** The runner's cadence. A run opened from the list may still be in flight. */
const POLL_MS = 2000;

export default function StandardRunViewer() {
  const { runId = '' } = useParams();
  const navigate = useNavigate();
  const numericId = Number(runId);

  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const run = useQuery({
    queryKey: ['reports', 'standard-run', numericId],
    queryFn: () => standardReportsService.getRun(numericId),
    enabled: Number.isFinite(numericId) && numericId > 0,
    retry: false,
    // Re-reads the whole run rather than the status poll: the preview arrives
    // with the run, and this page has nothing to show without it.
    refetchInterval: (q) => {
      const status = q.state.data?.status;

      return status && !isTerminalRunStatus(status) ? POLL_MS : false;
    },
  });

  // `reportKey` is what a run carries, and it is a usable fallback, but the
  // catalog holds the name a user actually picked the report by.
  const catalog = useQuery({
    queryKey: ['reports', 'standard-catalog'],
    queryFn: standardReportsService.catalog,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const download = async (row: StandardReportRun) => {
    setDownloading(true);
    setDownloadError(null);

    try {
      const blob = await standardReportsService.downloadRun(row.runId);
      saveBlob(blob, `${row.reportKey}_${row.runId}.xlsx`);
    } catch (e) {
      setDownloadError(getErrorMessage(e, 'Could not download the workbook.'));
    } finally {
      setDownloading(false);
    }
  };

  const back = (
    <button
      type="button"
      onClick={() => navigate('/reports/my-reports')}
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
    >
      <ArrowLeft className="h-4 w-4" />
      My Reports
    </button>
  );

  if (run.isLoading) {
    return (
      <div className="p-6">
        {back}
        <PageHeader variant="compact" title="Report" />
        <div className="flex items-center gap-2 text-slate-500">
          <Spinner size="sm" /> Loading report…
        </div>
      </div>
    );
  }

  if (run.isError || !run.data) {
    return (
      <div className="p-6">
        {back}
        <PageHeader variant="compact" title="Report" />
        <Alert variant="error" title="Could not open this report">
          {run.isError
            ? getErrorMessage(run.error, 'Try again in a moment.')
            : 'It is no longer available. Run the report again to get a new copy.'}
        </Alert>
      </div>
    );
  }

  // Narrowed to a definite value before use: reading `run.data` inside a
  // callback would not stay narrowed, and the title lookup is a callback.
  const data = run.data;

  const title =
    (catalog.data ?? []).find((d) => d.key === data.reportKey)?.title ||
    data.reportKey ||
    'Report';

  const period =
    data.windowStart && data.windowEnd
      ? `${data.windowStart.slice(0, 10)} → ${data.windowEnd.slice(0, 10)}`
      : 'All dates';

  return (
    <div className="p-6">
      {back}
      <PageHeader
        variant="compact"
        title={title}
        actions={
          // Hidden rather than disabled when there is no file: a greyed-out
          // button implies it might come back, and a cleared file does not.
          data.hasFile ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={() => void download(data)}
              disabled={downloading}
            >
              Download workbook
            </Button>
          ) : null
        }
      />

      <p className="mb-4 text-sm text-slate-500">{period}</p>

      {downloadError && <Alert variant="error">{downloadError}</Alert>}

      {!isTerminalRunStatus(data.status) && (
        <Alert variant="info" title="Still being prepared">
          This report has not finished yet. It will appear here on its own in a
          few moments.
        </Alert>
      )}

      {data.status === 'empty' && (
        <Alert variant="info" title="Nothing to show">
          Nothing matched the dates this report covered. Try running it over a
          wider period.
        </Alert>
      )}

      {data.status === 'too_large' && (
        <Alert variant="warning" title="Too much to show at once">
          {data.refusalReason ??
            'Run it again over a shorter period to bring it under the limit.'}
        </Alert>
      )}

      {data.status === 'failed' && <StandardRunFailedAlert />}

      {data.status === 'ready' &&
        (data.preview ? (
          <StandardReportSections
            preview={data.preview}
            truncated={data.truncated}
            totalRowCount={data.rowCount}
          />
        ) : (
          <Alert variant="info" title="Nothing to show here">
            This report finished, but there is nothing to display on screen.
            Download the workbook for the full result.
          </Alert>
        ))}

      {data.purged && data.status === 'ready' && (
        <p className="mt-4 text-sm text-slate-500">
          The saved file for this report has been cleared. Run it again to get a
          new copy to download.
        </p>
      )}
    </div>
  );
}
